import { Close } from '@mui/icons-material'
import { Box, Button, Dialog, DialogContent, IconButton, Typography } from '@mui/material'
import { Ref, useEffect, useRef, useState } from 'react'

import SearchPane, { SearchPaneHandle } from '../../../components/search/SearchPane'
import SearchPreviewPanel from '../../../components/search/SearchPreviewPanel'
import { TagDefinition } from '../../../components/tags/tagTreeUtils'
import { Book, SearchResult } from '../../../services/api'
import { ContentAST } from '../../../transforms/ast'

interface MobileSearchDialogProps {
  open: boolean
  onClose: () => void
  query: string
  results: SearchResult[]
  loading: boolean
  searched: boolean
  direction: 'ltr' | 'rtl'
  selectedResultKey: string | null
  selectedPreviewResult: SearchResult | null
  previewOpen: boolean
  previewHeightRatio: number
  previewDragging: boolean
  previewAst: ContentAST | null
  previewLoading: boolean
  previewError: string | null
  book: Book | null
  searchRef: Ref<SearchPaneHandle>
  onQueryChange: (value: string) => void
  onClear: () => void
  onNavigate: (filename: string, blockId: string, matchText: string, matchStart: number) => void
  onPreviewSelect: (filename: string, blockId: string, matchText: string, matchStart: number) => void
  onEnterSelection: () => void
  onClosePreview: () => void
  onPreviewHeaderPointerDown: (event: React.PointerEvent<HTMLDivElement>) => void
  // Tag filter props
  selectedTagIds?: string[]
  onTagsChange?: (tagIds: string[]) => void
  tags?: Record<string, TagDefinition>
  tagColorMap?: Record<string, string>
  tagUsageCounts?: Record<string, number>
  onOpenTagManager?: () => void
}

export default function MobileSearchDialog({
  open,
  onClose,
  query,
  results,
  loading,
  searched,
  direction,
  selectedResultKey,
  selectedPreviewResult,
  previewOpen,
  previewHeightRatio,
  previewDragging,
  previewAst,
  previewLoading,
  previewError,
  book,
  searchRef,
  onQueryChange,
  onClear,
  onNavigate,
  onPreviewSelect,
  onEnterSelection,
  onClosePreview,
  onPreviewHeaderPointerDown,
  selectedTagIds,
  onTagsChange,
  tags,
  tagColorMap,
  tagUsageCounts,
  onOpenTagManager,
}: MobileSearchDialogProps) {
  const contentRef = useRef<HTMLDivElement | null>(null)
  const [contentHeight, setContentHeight] = useState(0)
  const [keyboardInset, setKeyboardInset] = useState(0)
  const [viewportHeight, setViewportHeight] = useState(() =>
    typeof window !== 'undefined' ? window.visualViewport?.height || window.innerHeight : 0,
  )

  useEffect(() => {
    const element = contentRef.current
    if (!element) {
      return
    }

    const updateHeight = () => setContentHeight(element.clientHeight)
    updateHeight()

    const observer = new ResizeObserver(updateHeight)
    observer.observe(element)
    return () => {
      observer.disconnect()
    }
  }, [])

  useEffect(() => {
    const updateKeyboardInset = () => {
      const viewport = window.visualViewport
      if (!viewport) {
        setKeyboardInset(0)
        return
      }

      // Estimate the area obscured by the on-screen keyboard.
      const obscuredHeight = Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop)
      setKeyboardInset(obscuredHeight)
    }

    updateKeyboardInset()
    const viewport = window.visualViewport
    if (!viewport) {
      return
    }

    viewport.addEventListener('resize', updateKeyboardInset)
    viewport.addEventListener('scroll', updateKeyboardInset)
    window.addEventListener('orientationchange', updateKeyboardInset)

    return () => {
      viewport.removeEventListener('resize', updateKeyboardInset)
      viewport.removeEventListener('scroll', updateKeyboardInset)
      window.removeEventListener('orientationchange', updateKeyboardInset)
    }
  }, [])

  useEffect(() => {
    const updateViewportHeight = () => {
      setViewportHeight(window.visualViewport?.height || window.innerHeight)
    }

    updateViewportHeight()
    window.addEventListener('resize', updateViewportHeight)
    window.addEventListener('orientationchange', updateViewportHeight)
    const viewport = window.visualViewport
    viewport?.addEventListener('resize', updateViewportHeight)
    viewport?.addEventListener('scroll', updateViewportHeight)

    return () => {
      window.removeEventListener('resize', updateViewportHeight)
      window.removeEventListener('orientationchange', updateViewportHeight)
      viewport?.removeEventListener('resize', updateViewportHeight)
      viewport?.removeEventListener('scroll', updateViewportHeight)
    }
  }, [])

  const effectiveLayoutHeight = contentHeight > 0 ? contentHeight : viewportHeight
  const previewOverlayHeight = previewOpen ? effectiveLayoutHeight * previewHeightRatio : 0
  const previewExtraInset = previewOpen ? 56 : 0
  const resultsBottomInset = previewOverlayHeight + keyboardInset + previewExtraInset

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          bgcolor: 'var(--bg-app)',
        },
      }}
    >
      <DialogContent
        ref={contentRef}
        sx={{
          p: 0,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <Box
          sx={{
            px: 1.5,
            py: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid var(--border-subtle)',
            bgcolor: 'var(--bg-surface)',
            flexShrink: 0,
          }}
        >
          <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--text-primary)' }}>Search</Typography>
          <IconButton size="small" onClick={onClose} aria-label="Close search" sx={{ color: 'var(--icon-default)' }}>
            <Close fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, bgcolor: 'var(--bg-surface)' }}>
          <SearchPane
            ref={searchRef}
            query={query}
            results={results}
            loading={loading}
            searched={searched}
            direction={direction}
            onQueryChange={onQueryChange}
            onClear={onClear}
            onNavigate={onNavigate}
            onPreviewSelect={onPreviewSelect}
            onEnterSelection={onEnterSelection}
            autoFocus={open}
            selectedResultKey={selectedResultKey}
            resultsBottomInset={resultsBottomInset}
            selectedTagIds={selectedTagIds}
            onTagsChange={onTagsChange}
            tags={tags}
            tagColorMap={tagColorMap}
            tagUsageCounts={tagUsageCounts}
            onOpenTagManager={onOpenTagManager}
          />
        </Box>

        {previewOpen && (
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              height: `${previewHeightRatio * 100}%`,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              bgcolor: 'var(--bg-surface)',
              borderTop: '2px solid var(--border-primary)',
              borderTopLeftRadius: 18,
              borderTopRightRadius: 18,
              boxShadow: '0 -12px 30px var(--shadow-color)',
              zIndex: 2,
            }}
          >
            <SearchPreviewPanel
              sourceFilename={selectedPreviewResult?.filename}
              previewIdentity={selectedPreviewResult ? `content:${selectedPreviewResult.filename}` : null}
              previewTitle={selectedPreviewResult?.chapterTitle || null}
              previewAst={previewAst}
              previewLoading={previewLoading}
              previewError={previewError}
              previewBlockId={selectedPreviewResult?.blockId || null}
              previewHighlightText={
                selectedPreviewResult && selectedPreviewResult.matchStart < selectedPreviewResult.matchEnd
                  ? selectedPreviewResult.snippet.slice(
                      selectedPreviewResult.matchStart,
                      selectedPreviewResult.matchEnd,
                    )
                  : null
              }
              previewHighlightOffset={
                selectedPreviewResult && selectedPreviewResult.blockMatchStart > 0
                  ? selectedPreviewResult.blockMatchStart
                  : null
              }
              book={book}
              onHeaderPointerDown={onPreviewHeaderPointerDown}
              headerDraggable={true}
              headerDragging={previewDragging}
              headerActions={
                <>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={onEnterSelection}
                    sx={{
                      color: 'var(--text-secondary)',
                      borderColor: 'var(--border-subtle)',
                      fontWeight: 500,
                      textTransform: 'none',
                      minWidth: 'auto',
                      px: 1.25,
                    }}
                  >
                    Open
                  </Button>
                  <IconButton
                    size="small"
                    onClick={onClosePreview}
                    aria-label="Close preview"
                    sx={{ color: 'var(--icon-default)' }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </>
              }
            />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  )
}
